import React, { useEffect } from 'react';
import withStyles from '@material-ui/core/styles/withStyles';
import { Button, Dialog, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem } from '@material-ui/core';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { validationErrorToSubmissionError } from 'utils/FormUtils';
import DefaultError from 'ui/base/DefaultError';
import { displayAlert } from 'reducers/alerts';
import ValidatedSelectField from '../../ui/base/ValidatedSelectField';
import { connect } from 'react-redux';
import {LocaleConsumer} from "containers/locale/LocaleProvider";
import { fetchInstitutionUsers, fetchPlayerUsers, putInstitutionCaregiver, usersSelector } from '../../reducers/users';
import { compose } from 'recompose';

const styles = {
    dialog: {
        width: '60%',
    },
};

function PlayerAddCaregiverModalCareCircle({
    institutionCaregivers,
    playerUsers,
    onRequestClose,
    reset,
    open,
    classes,
    error,
    handleSubmit,
    submitting,
    institutionId,
    playerId,
    fetchInstitutionCaregivers,
    fetchPlayerUsers
}) {
    useEffect(() => {
        
        if (open) {
            // Fetch caregivers and player users when the modal opens
            if (playerId) fetchPlayerUsers(playerId);
            if (institutionId) fetchInstitutionCaregivers(institutionId);
        }
    }, [open, playerId, institutionId, fetchInstitutionCaregivers, fetchPlayerUsers]);

    const handleRequestClose = () => {
        reset();
        onRequestClose();
    };

    const caregiversToAdd = institutionCaregivers.filter(
        (caregiver) => !playerUsers.find((v) => v.id === caregiver.id)
    );
    
    return (
        <LocaleConsumer>
            {({ getTranslation }) => (
                <Dialog
                    onClose={handleRequestClose}
                    open={open}
                    classes={{ paper: classes.dialog }}
                >
                    <DialogTitle>
                        {getTranslation("player_modal_share_player_with_a_care_staff")}
                    </DialogTitle>
                    <DialogContent>
                        {error && <DefaultError>{error}</DefaultError>}
                        <FormControl fullWidth>
                            <InputLabel htmlFor="email">
                                {getTranslation("player_modal_select_a_caregiver_from_your_care_team")}
                            </InputLabel>
                            <Field name="email" id="email" required component={ValidatedSelectField}>
                                {caregiversToAdd.map((user) => (
                                    <MenuItem key={user.id} value={user.email}>
                                        {user.email}
                                    </MenuItem>
                                ))}
                            </Field>
                        </FormControl>
                        <br />
                        <br />
                        <div style={{ display: 'flex' }}>
                            <div style={{ flex: 1 }} />
                            <Button onClick={handleRequestClose} disabled={submitting}>
                                {getTranslation("cancel")}
                            </Button>
                            <Button color="primary" 
                                onClick={handleSubmit} 
                                disabled={submitting} 
                                style={{ marginLeft: 5 }}
                            >
                                {getTranslation("send")}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </LocaleConsumer>
    );
}

export default compose(
    connect(
        (state) => ({
            institutionCaregivers: usersSelector.getInstitutionUsers(state),
            playerUsers: usersSelector.getCurrentPlayerUsers(state),
        }),
        (dispatch) => ({
            fetchInstitutionCaregivers: (institutionId) => dispatch(fetchInstitutionUsers(institutionId)),
            fetchPlayerUsers: (playerId) => dispatch(fetchPlayerUsers(playerId))
        })
    ),
    withStyles(styles),
    reduxForm({
        form: 'addCaregiverToPlayerCareCircle',
        enableReinitialize: true,
        onSubmit: (values, dispatch, props) => {
            const { institutionId, playerId, institutionCaregivers } = props;
            const selectedCaregiver = institutionCaregivers.find(c => c.email === values.email);

            if (!selectedCaregiver) {
                throw new SubmissionError({
                    email: 'Selected caregiver is not valid.',
                });
            }

            const caregiverId = selectedCaregiver.id;

            // Merge logic
            const existingSharedPlayers = selectedCaregiver.sharedPlayers || [];
            const updatedSharedPlayers = Array.from(new Set([...existingSharedPlayers, playerId]));

            return dispatch(
                putInstitutionCaregiver(institutionId, caregiverId, {
                    sharedPlayers: updatedSharedPlayers
                })
            )
            .then(() => {
                dispatch(displayAlert(`An institutional caregiver has been added to the care circle of this player.`));
                return Promise.all([
                    dispatch(fetchPlayerUsers(props.playerId)),
                    dispatch(fetchInstitutionUsers(institutionId))
                ]).then(() => {
                    props.reset();
                    props.onRequestClose();
                });
            })
            .catch((error) => {
                throw validationErrorToSubmissionError(error);
            });
        }
    })
)(PlayerAddCaregiverModalCareCircle);